import { getTeamsDateFormat, getTeamsTimeFormat } from "../utils/dateUtils";
import { CustomerMeeting } from "../../customerMeetings/store/CustomerMeeting";

const sk = {
    common: {
        newMeeting: "Nová schôdza",
        errorMessage: (errorDetails: string) => `Vyskytla sa chyba  ${errorDetails}`,
        loader: "Načítanie údajov...",
    },
    appOutsideMsTeams: {
        message: "Aplikácia musí byť spustená v kontexte Microsoft Teams",
    },
    logo: {
        corporation: "Logo mBank pre firmy",
        retail: "Logo mBank pre retailových zákazníkov",
        private: "Logo mBank pre privátne bankovníctvo",
    },
    meetingModals: {
        closeButton: "Zatvoriť",
        startingHourLabel: "Štart",
        endingHourLabel: "Ukončiť",
        titlePlaceholder: "Pridať názov",
        customerNamePlaceholder: "Imię i nazwisko klienta",
        customerEmailPlaceholder: "Adres e-mail klienta",
        customerIdPlaceholder: "ID klienta",
        additionalAttendeeEmailsPlaceholder: "E-mailové adresy ostatných konzultantov",
        englishCommunicationCheckboxLabel: "Odoslať komunikáciu v angličtine",
        addClient: "Dodaj klienta",
        add: "Dodaj",
        fullnamePlaceholder: "Imię i nazwisko",
        emailPlaceholder: "Adres e-mail",
        useVirtualAppointments: "Use Virtual Appointment",
        customers: "Klienci",
        consultants: "Dodatkowi pracownicy",
        externalConsultants: "Konsultanci zewnętrzni",
        thirdParties: "Osoby trzecie",
        consultantsToolip: "np. asystent/doradca/DR/DSM/BM/mTFI",
        externalConsultantsTooltip: "np. pracownicy kancelarii, z którymi mamy podpisane umowy",
        thirdPartiesTooltip: "np. prawnicy, mąż, sekretarka - osoby bez umocowania do rachunku",
        customersTooltip: "potencjalni klienci",
        validation: {
            invalidEmail: "E-mail je nesprávny",
            invalidAttendees: "Jedna z uvedených e-mailových adries je nesprávna",
            invalidRegon: "Uvedené IČO je nesprávne",
            invalidClientId: "Nesprávny formát ID zákazníka",
            invalidC6: "Nesprávny formát čísla C6",
            invalidTitle: "Obsah nemôže obsahovať nasledujúce znaky: <>",
            invalidSeparator: "Použite bodkočiarku ako oddeľovač",
            invalidMeetingEndingDate: (maxNumberOfDays: number) =>
                `Dátum ukončenia stretnutia nesmie presiahnuť ${maxNumberOfDays} dní`,
        },
        corporate: {
            addContactPerson: "Dodaj osobę kontaktową",
            companyNamePlaceholder: "Meno spoločnosti",
            c6NumberPlaceholder: "Číslo C6",
            regonPlaceholder: "IČO",
        },
    },
    createMeeting: {
        header: "Nová schôdza",
        selectBusinessLinePlaceholder: "Vybrať obchodnú líniu",
        loadingBusinessLines: "Načítanie obchodných línií...",
        noBusinessLineFound: "Nie je možné vytvoriť novú schôdzu, pretože nepatríte do žiadnej obchodnej línie.",
        createButton: "Vytvoriť schôdzu",
        adding: "Vytvára sa schôdza...",
    },
    updateMeeting: {
        header: "Upraviť schôdzu",
        updateButton: "Upraviť schôdzu",
        updating: "Aktualizácia schôdze...",
        newAdditionalAttendeeEmailsPlaceholder: "Ďalšie e-mailové adresy ostatných konzultantov",
        save: "Uložiť",
    },
    meetingsList: {
        noMeetings: "Nie sú naplánované žiadne schôdze",
        headers: {
            date: "Dátum schôdze",
            attendees: "Zákazníci",
            subject: "Téma schôdze",
        },
        cancelActionButton: "Zrušiť schôdzu",
        cancelDialog: {
            message: (meeting: CustomerMeeting) =>
                `Naozaj chcete zrušiť schôdzu ${meeting.customers
                    .map((customer) => customer.name)
                    .join(", ")} (${getTeamsDateFormat(meeting.startAt)} v čase ${getTeamsTimeFormat(
                    meeting.startAt
                )} – ${getTeamsTimeFormat(meeting.endAt)})?`,
            additionalMessage: "Všetci účastníci schôdze dostanú správu s informáciou o jeho zrušení.",
            header: "Chcete zrušiť schôdzu?",
            cancelButton: "Ukončiť",
            confirmButton: "Potvrdiť",
        },
        updateButton: "Upraviť",
        joinButton: "Začať",
        loading: "Načítanie schôdzí...",
        updateMeetingSuccessAlert: "Schôdza bola úspešne aktualizovaná",
        updateMeetingTypeButton: "Typ schôdze",
    },
    businessLines: {
        private: "Súkromná",
        corporate: "Korporátna",
        retail: "Retailová",
    },
    organizerNoRecordingWarning: {
        content: (time: string) =>
            `Nahrávanie sa prerušilo. Zapnite ho do ${time} sekúnd – ak tak neurobíte, schôdza sa automaticky skončí.`,
    },
    inMeetingAppConfig: {
        header: "Schôdza so zákazníkmi na diaľku",
        info: "Aplikácia na ovládanie procesu schôdzí so zákazníkmi na diaľku",
    },
    beforeMeeting: {
        header: "Schôdza so zákazníkom na diaľku",
        organizatorSubHeader: "Ste organizátorom tejto schôdze",
        presenterSubHeader: "Na tejto schôdzi ste konzultantom",
        rulesHeader: "Pred začatím schôdze so zákazníkom (udelením prístupu zákazníkovi do chatovacej miestnosti):",
        setBackgroundRule: "Nastavte logo banky ako pozadie.",
        rememberHeader: "Nezabudnúť!",
        cautionHeader: "Pozor!",
        notEnoughDiskSpace:
            "Nedostatok miesta na záznam schôdze v službe OneDrive. Potrebujete 1 GB miesta. Odstráňte nepotrebné súbory zo služby OneDrive.",
        meetingRules: {
            header: "Na schôdzach so zákazníkom:",
            noDesktopSharingRule:
                "Nikdy nezdieľajte svoju pracovnú plochu. Môžete sprístupniť len povolené aplikácie uvedené v pracovnom postupe.",
            noLinksOpenRule: "Neotvárajte žiadne odkazy odoslané cez chat.",
        },
        organizerRules: {
            recordRule: "Začnite nahrávanie schôdze.",
            alwaysRecordRule: "Vypnutím nahrávania schôdze sa schôdza okamžite preruší.",
        },
    },
    duringMeeting: {
        organizerView: {
            header: "Kontrolný zoznam schôdze",
            freeSpaceRequirement: "1 GB voľného miesta v službe OneDrive",
            isRecordingRequirement: "Organizátor spustil nahrávanie",
            identityRequirement: "Potvrdiť totožnosť zákazníka",
        },
        presenterView: {
            header: "Na tejto schôdzi ste konzultantom",
        },
    },
    organizerMeetingTypePrompt: {
        headerOnStartNotAnOrganizer: "Organizátor vyplní údaje o schôdzi",
        headerOnStartOrganizer: "Vyberte druh rozhovoru",
        headerOnFinish: "Ďakujeme za vyplnenie dotazníka",
        placeholderMeetingType: "Druh rozhovoru",
        save: "Uložiť",
    },
    customerJoinsLobbyPrompt: {
        content:
            "Musíme nahrávať schôdze so zákazníkmi. ZAPNITE NAHRÁVANIE, skôr ako sa zákazník pripojí k schôdzi. Ak to neurobíte alebo prerušíte nahrávanie – schôdza sa automaticky skončí.",
    },
    screenSharingWarningPrompt: {
        defaultContent: "Nezabúdajte, že počas rozhovoru môžete sprístupniť iba povolené súbory a aplikácie.",
        privateContent:
            "Nezabúdajte, že počas rozhovoru môžete sprístupniť iba povolené súbory a aplikácie. Ak chcete sprístupniť aplikáciu Poradca, nastavte v nej video režim.",
    },
    peoplePicker: {
        enterCharacters: "Zadajte aspoň 3 znaky",
        noResults: "Nenašli sa žiadne výsledky",
        loading: "Vyhľadávanie",
        moreResults:
            "Znaleziono więcej wyników, spróbuj doprecyzować zapytanie jeśli lista nie zawiera szukanego elementu.",
    },
};

export default sk;
